import authService from "./services/auth-service"
import { cabecalhosDoApp, detectarFusoIana as fusoDoAparelho } from "./cabecalhos-do-app"
import { fraseDoStatus, mensagemDaApi } from "./utils/erro-da-api"

const BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.welltrainer.com.br"

const DEFAULT_TIMEOUT = 8000

// Upload de arquivo (multipart) nao cabe em 8s: uma foto de iPhone tem alguns MB
// e ainda passa por processamento de imagem/video no servidor antes da resposta.
// Com o timeout curto o AbortController cancelava a request no meio do upload e
// o usuario via "erro na requisicao" mesmo com a conexao funcionando.
const UPLOAD_TIMEOUT = 120000

export interface ApiFetchOptions extends RequestInit {
  /** Sobrescreve o timeout em ms (padrao: 8s, ou 120s quando o body e FormData). */
  timeout?: number
}

function resolverTimeout(options: ApiFetchOptions): number {
  if (typeof options.timeout === "number") return options.timeout
  const ehUpload =
    typeof FormData !== "undefined" && options.body instanceof FormData
  return ehUpload ? UPLOAD_TIMEOUT : DEFAULT_TIMEOUT
}

/**
 * Um fetch que falha (sem internet, CORS, DNS, API fora, conexão resetada pelo
 * proxy) rejeita com TypeError e mensagem crua do browser: "Failed to fetch"
 * (Chrome), "NetworkError when attempting to fetch resource." (Firefox), "Load
 * failed" (Safari). Casamos pela mensagem, e não por `instanceof TypeError`
 * pra não engolir TypeError de bug de código, que deve continuar aparecendo.
 */
const MENSAGENS_DE_REDE = [
  "failed to fetch",
  "networkerror",
  "load failed",
  "network request failed",
  "network error",
]

export function ehFalhaDeRede(error: unknown): boolean {
  const msg = String((error as Error)?.message || "").toLowerCase()
  if (!msg) return false
  return MENSAGENS_DE_REDE.some((m) => msg.includes(m))
}

/** A frase de quando não chegou resposta que se possa ler. */
export const MENSAGEM_SEM_CONEXAO =
  "Sem conexão com o servidor. Verifique sua internet e tente novamente."

/**
 * Uma escrita barrada porque a assinatura do personal está vencida (modo leitura).
 *
 * O servidor responde 403 com `assinatura_vencida: true` e os números da cobrança.
 * Aqui isso vira um evento de janela, e não uma chamada direta a componente, porque
 * este arquivo não sabe nada de React: quem escuta é o `AvisoAssinaturaVencida`,
 * montado uma vez na casca do personal.
 */
export const EVENTO_ASSINATURA_VENCIDA = "welltrainer:assinatura-vencida"

export interface AssinaturaVencidaDetalhe {
  valor: number | null
  vencimento: string | null
  dias_vencido: number | null
  link_pagamento: string | null
}

/**
 * O último 403 de assinatura vencida, guardado no módulo.
 *
 * A tela dispara as requisições no mesmo instante em que monta, então o 403 pode
 * chegar ANTES de o componente de aviso existir e o evento cair no vazio. Guardar o
 * último detalhe aqui faz o aviso aparecer mesmo nesse caso.
 */
export const DETALHE_ASSINATURA_VENCIDA: { ultimo: AssinaturaVencidaDetalhe | null } = {
  ultimo: null,
}

function avisarAssinaturaVencida(data: any) {
  if (typeof window === "undefined") return
  const detalhe: AssinaturaVencidaDetalhe = {
    valor: data?.valor ?? null,
    vencimento: data?.vencimento ?? null,
    dias_vencido: data?.dias_vencido ?? null,
    link_pagamento: data?.link_pagamento ?? null,
  }
  DETALHE_ASSINATURA_VENCIDA.ultimo = detalhe
  window.dispatchEvent(new CustomEvent(EVENTO_ASSINATURA_VENCIDA, { detail: detalhe }))
}

export class ApiError extends Error {
  status: number
  data: any
  constructor(status: number, message: string, data: any) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.data = data
  }
}

/**
 * A frase de um erro para o aviso da tela: a que o servidor mandou (o
 * `mensagemDaApi` já tirou o que não foi escrito para gente) ou a da própria
 * tela. Erro que não nasceu de uma resposta do servidor (bug de código, a página
 * de login do Wi-Fi no lugar do JSON) traz o texto cru do navegador, e esse não
 * vai para a tela.
 */
export function fraseDoErro(erro: unknown, padrao: string): string {
  return erro instanceof ApiError && erro.message ? erro.message : padrao
}

/**
 * O corpo de uma resposta que deu certo.
 *
 * Com `response.json()` direto, três casos rejeitavam com o texto cru do
 * navegador, e é esse texto que as telas põem no aviso (`err?.message || ...`):
 *
 * - 2xx sem corpo: "Unexpected end of JSON input". É a rota que devolveu
 *   `null` (o Nest responde vazio), e continua rejeitando com um `Error` sem
 *   status, do jeito do `SyntaxError` de antes: quem chama trata esse caso pela
 *   falha, e virar `null` mudaria o caminho de cada uma dessas telas. Muda só
 *   o texto.
 * - 2xx que não é JSON, que é a página de login do Wi-Fi (o portal cativo
 *   responde 200 a qualquer endereço): "Unexpected token '<'...".
 * - a conexão que cai no meio da leitura: "Load failed", "network error".
 *
 * Os dois últimos são, para quem chama, o mesmo que ficar sem internet, e saem
 * como o `ApiError` de status 0 da falha de rede. Como `SyntaxError`, as telas
 * do treino (`isNetworkError`) não viam a falta de rede, e o registro que devia
 * ir para a fila offline era dado como falha.
 *
 * Devolve a promessa sem esperar por ela, como o `return response.json()` de
 * antes: a leitura do corpo continua fora do relógio do limite.
 */
export function lerCorpoDaResposta<T>(response: Response): Promise<T> {
  return response.text().then(
    (texto) => {
      if (!texto) throw new Error(fraseDoStatus(response.status))
      try {
        return JSON.parse(texto) as T
      } catch {
        throw new ApiError(0, MENSAGEM_SEM_CONEXAO, null)
      }
    },
    () => {
      throw new ApiError(0, MENSAGEM_SEM_CONEXAO, null)
    },
  )
}

/**
 * O fuso IANA do aparelho. Mora em `lib/cabecalhos-do-app.ts` e continua exportado daqui
 * para quem já importava. Função comum, e não `export { } from`: com a reexportação, o
 * next dev deixava de achar este arquivo no `import()` dinâmico do `auth-service`.
 */
export function detectarFusoIana(): string | null {
  return fusoDoAparelho()
}

async function getHeaders(): Promise<Record<string, string>> {
  const token = await authService.getValidToken()
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers["Authorization"] = token
  }
  return Object.assign(headers, cabecalhosDoApp())
}

async function doFetch<T>(
  path: string,
  options: RequestInit,
  signal: AbortSignal,
  /** Liga o relógio do limite: chamado depois do token, logo antes do envio. */
  comecarRelogio: () => void,
): Promise<{ response: Response; ok: boolean }> {
  const baseHeaders = await getHeaders()
  const mergedHeaders: Record<string, string> = {
    ...baseHeaders,
    ...(options.headers as Record<string, string>),
  }
  // Remove headers with empty value (allows overriding Content-Type for FormData)
  for (const key of Object.keys(mergedHeaders)) {
    if (mergedHeaders[key] === "") delete mergedHeaders[key]
  }
  comecarRelogio()
  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    signal,
    headers: mergedHeaders,
    cache: "no-store",
  })
  return { response, ok: response.ok }
}

export async function apiFetch<T = any>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const controller = new AbortController()
  const limite = resolverTimeout(options)
  /* O relógio começa quando o pedido SAI, e não quando a função é chamada.

     Antes de sair, o pedido espera o token, e renovar o token é uma ida ao
     Cognito que pode levar segundos (o próprio `getValidToken` só desiste dele
     em 8s e cai na renovação pela nossa API). Com o relógio ligado já na
     chamada, essa espera comia o limite inteiro: o `abort` disparava antes do
     `fetch` e o pedido era cancelado sem nem sair do celular. Como todas as
     buscas da tela esperam o MESMO token, elas falhavam juntas, e nenhuma
     chegava ao servidor para aparecer no `api_log`.

     Religar a cada envio faz a segunda tentativa, a do 401, ganhar o limite
     inteiro também. */
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const comecarRelogio = () => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => controller.abort(), limite)
  }

  try {
    let { response } = await doFetch<T>(path, options, controller.signal, comecarRelogio)

    // Se 401, tentar refrescar o token via Cognito SDK / API e RETRY.
    // Se o refresh funcionar, a sessao segue. Se nao, sessao esta irrecuperavel
    // (ex: WKWebView iOS limpou o storage do Cognito SDK), redirecionar pra
    // /login em vez de deixar a tela em branco.
    if (response.status === 401 && typeof window !== "undefined") {
      /* A renovação do token não conta no limite: o relógio para aqui e volta
         a correr quando o pedido sai de novo. */
      clearTimeout(timeoutId)
      let novoToken: string | null = null
      try {
        novoToken = await authService.forceRefreshToken()
        if (novoToken) {
          const retryResult = await doFetch<T>(path, options, controller.signal, comecarRelogio)
          response = retryResult.response
        }
      } catch {}

      if (response.status === 401) {
        // Sem refresh possivel, sessao perdida. Mandar pra login.
        if (!novoToken) authService.handleSessionLost()
        let errorData: any = {}
        try { errorData = await response.json() } catch {}
        throw new ApiError(
          401,
          mensagemDaApi(errorData, fraseDoStatus(401)),
          errorData,
        )
      }
    }

    if (!response.ok) {
      let errorData: any = {}
      try {
        errorData = await response.json()
      } catch {}

      /* Modo leitura: avisa a casca antes de propagar. O erro segue subindo para
         quem chamou continuar tratando a falha da sua própria ação. */
      if (response.status === 403 && errorData?.assinatura_vencida) {
        avisarAssinaturaVencida(errorData)
      }

      /* Conta banida (ou aparelho barrado) com a sessão já aberta: a mesma tela que o
         login mostra, em vez de cada pedaço da tela dizer "Não foi possível carregar".
         A API só manda `bloqueado: true` nesse caso. */
      if (
        response.status === 403 &&
        errorData?.bloqueado === true &&
        typeof window !== "undefined" &&
        window.location.pathname !== "/conta-bloqueada"
      ) {
        window.location.assign("/conta-bloqueada")
      }

      throw new ApiError(
        response.status,
        /* A validação de DTO responde com `message` em ARRAY, uma frase por
           campo: lendo direto, a tela mostrava "[object Object]" ou caía no
           texto genérico. Sem frase que sirva, vale a do status. */
        mensagemDaApi(errorData, fraseDoStatus(response.status)),
        errorData,
      )
    }

    return lerCorpoDaResposta<T>(response)
  } catch (error: any) {
    if (error.name === "AbortError") {
      throw new ApiError(0, "A conexão está lenta. Tente novamente.", null)
    }
    // ApiError já é erro normalizado da própria API, repassa intacto pra não
    // confundir uma mensagem do servidor com falha de rede.
    if (error instanceof ApiError) throw error
    if (ehFalhaDeRede(error)) {
      // A mensagem crua do browser vazava pros toasts (que fazem `err.message ||
      // "..."`): o usuário via "NetworkError when attempting to fetch resource".
      console.error("[api] falha de rede:", error)
      throw new ApiError(0, MENSAGEM_SEM_CONEXAO, null)
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}
